import {
  getReaderHealth,
  listSelectableTerminals,
  rememberTerminal,
  type SelectableTerminalDto,
} from "./api.ts";

interface ReaderContext {
  identity: string;
  apiBaseUrl: string;
  tenantId: string;
  currentReaderId: string | undefined;
}

/** The floor dropdown owns only transient view state. The server owns reader eligibility. */
export function createReaderDropdown(options: {
  panel: HTMLElement;
  triggers: readonly HTMLButtonElement[];
  context: () => ReaderContext | undefined;
  selected: (reader: SelectableTerminalDto) => void;
  /** UIFIX5: where "Set up a reader" goes. Returns undefined when this person has no way to set one up, and the
   * row is then not drawn. */
  setup?: () => (() => void) | undefined;
}): { close: () => void } {
  let generation = 0;
  const close = (): void => {
    generation++;
    options.panel.hidden = true;
    for (const trigger of options.triggers) trigger.setAttribute("aria-expanded", "false");
  };
  const current = (sequence: number, context: ReaderContext): boolean =>
    sequence === generation &&
    !options.panel.hidden &&
    options.context()?.identity === context.identity;
  const message = (text: string): void => {
    const status = document.createElement("p");
    status.setAttribute("role", "status");
    status.textContent = text;
    options.panel.replaceChildren(status);
  };
  const open = async (trigger: HTMLButtonElement): Promise<void> => {
    if (!options.panel.hidden) {
      close();
      return;
    }
    const context = options.context();
    if (context === undefined) return;
    const sequence = ++generation;
    options.panel.hidden = false;
    for (const button of options.triggers)
      button.setAttribute("aria-expanded", button === trigger ? "true" : "false");
    const rect = trigger.getBoundingClientRect();
    options.panel.style.top = `${rect.bottom + 8}px`;
    const panelWidth = options.panel.getBoundingClientRect().width;
    const left = Math.max(8, Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - 8));
    options.panel.style.left = `${left}px`;
    options.panel.style.right = "auto";
    message("Loading readers");
    const result = await listSelectableTerminals(context.apiBaseUrl, context.tenantId);
    if (!current(sequence, context)) return;
    if (result.status !== 200) {
      message(result.body.message ?? "Could not load card readers. Check your connection.");
      return;
    }
    if (result.body.terminals.length === 0) {
      message("No readers available");
      const goSetup = options.setup?.();
      if (goSetup !== undefined) {
        const row = document.createElement("button");
        row.type = "button";
        row.className = "reader-dropdown-choice reader-dropdown-setup";
        row.dataset["testid"] = "reader-dropdown-setup";
        row.textContent = "Set up a reader";
        row.addEventListener("click", () => {
          close();
          goSetup();
        });
        options.panel.append(row);
      }
      return;
    }
    const heading = document.createElement("h2");
    heading.textContent = "Card readers";
    options.panel.replaceChildren(heading);
    for (const reader of [...result.body.terminals].sort((a, b) =>
      a.display_name.localeCompare(b.display_name),
    )) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "reader-dropdown-choice";
      const name = document.createElement("span");
      name.textContent = reader.display_name;
      const status = document.createElement("span");
      status.className = "reader-dropdown-status";
      status.textContent = "Status unavailable";
      button.append(name, status);
      if (reader.id === context.currentReaderId) {
        const selected = document.createElement("span");
        selected.textContent = "✓ Current";
        button.append(selected);
      }
      options.panel.append(button);
      void getReaderHealth(context.apiBaseUrl, context.tenantId, reader.id).then((health) => {
        if (!current(sequence, context) || !button.isConnected) return;
        status.textContent =
          health.status === 200 && health.body.health.terminalId === reader.id
            ? health.body.health.state === "online"
              ? "Online"
              : health.body.health.state === "offline"
                ? "Offline"
                : "Status unavailable"
            : "Status unavailable";
      });
      button.addEventListener("click", () => {
        if (!current(sequence, context)) return;
        for (const choice of options.panel.querySelectorAll<HTMLButtonElement>("button"))
          choice.disabled = true;
        void rememberTerminal(context.apiBaseUrl, context.tenantId, { terminalId: reader.id }).then(
          (saved) => {
            if (!current(sequence, context)) return;
            if (saved.status !== 200) {
              message(saved.body.message ?? "Could not select this card reader. Try again.");
              return;
            }
            options.selected(reader);
            close();
          },
        );
      });
    }
  };
  for (const trigger of options.triggers)
    trigger.addEventListener("click", () => void open(trigger));
  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Node)) return;
    if (
      !options.panel.contains(event.target) &&
      !options.triggers.some((trigger) => trigger.contains(event.target as Node))
    )
      close();
  });
  window.addEventListener("resize", close);
  return { close };
}
